
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { PageLayout } from '@/components/layout/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { WorkoutComplete } from '@/components/celebrations/WorkoutComplete';
import { WorkoutExerciseList } from '@/components/workout/WorkoutExerciseList';
import { WorkoutExerciseContent } from '@/components/workout/WorkoutExerciseContent';
import { useWorkout } from '@/hooks/use-workout';

const Workout = () => {
  const { user } = useAuth();
  
  const {
    currentExerciseIndex,
    setCurrentExerciseIndex,
    workoutExercises,
    currentExercise,
    userAnswers,
    completed,
    workoutProgress,
    feedback,
    isLoadingFeedback,
    showCelebration,
    streakCount,
    handleAnswerChange,
    handleCompleteExercise,
    handleNextExercise,
    handlePreviousExercise,
    handleCloseCelebration
  } = useWorkout(user);

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold font-lexend">Today's AI Workout</h1>
              <p className="text-muted-foreground">Complete these exercises to strengthen your AI skills</p>
            </div>
            <div className="flex items-center bg-secondary/70 px-4 py-2 rounded-full">
              <Clock className="text-primary h-5 w-5 mr-2" />
              <span className="text-sm font-medium">Estimated time: 20 minutes</span>
            </div>
          </div>
          
          <Progress value={workoutProgress} className="h-2" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <WorkoutExerciseList
              workoutExercises={workoutExercises}
              currentExerciseIndex={currentExerciseIndex}
              completed={completed}
              onSelectExercise={setCurrentExerciseIndex}
            />
          </div>

          <div className="lg:col-span-2">
            <WorkoutExerciseContent
              currentExercise={currentExercise}
              userAnswer={userAnswers[currentExerciseIndex] || ''}
              isCompleted={completed.includes(currentExerciseIndex)}
              feedback={feedback[currentExerciseIndex]}
              isLoadingFeedback={isLoadingFeedback}
              onAnswerChange={handleAnswerChange}
              onComplete={handleCompleteExercise}
              onPrevious={handlePreviousExercise}
              onNext={handleNextExercise}
              canGoNext={currentExerciseIndex < workoutExercises.length - 1}
              canGoPrevious={currentExerciseIndex > 0}
            />
          </div>
        </div>
        
        <AnimatePresence>
          {showCelebration && (
            <WorkoutComplete 
              onClose={handleCloseCelebration} 
              streakCount={streakCount}
            />
          )}
        </AnimatePresence>
      </div>
    </PageLayout>
  );
};

export default Workout;
